import React, {FC, ReactNode} from 'react';
import {connect} from 'react-redux';

import {computeJobProgress, isContactsCsvImportJob} from '../../../../../../../../src/helpers/jobHelpers';
import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {ApiJob, JobStatusesEnum} from '../../../../../../../../src/models/api/jobModel';
import {FetchCardsByNamespaceOptions} from '../../../../../../../../src/store/data/cards/cardsActions';
import {
  getContactsManagerTableState,
  makeGetContactsManagerTableOffsetPaginationData,
  makeGetContactsManagerTableState,
  makeGetContactsTableCardIds,
  makeGetIsInMultiSelectMode,
  makeGetSelectedCardIds,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {
  contactsManagerFetchContactsTableDataAsyncAction,
  contactsManagerSetUpContactsTableAction,
  contactsManagerTableAddSelectedCardIdsAction,
  contactsManagerTableCleanupAction,
  contactsManagerTableRemoveSelectedCardIdsAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {tableStateIsContactsTableState} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {usePushEvent} from '../../../../../../../../srcCommon/push/pushContext';
import {PushEventTypesEnum} from '../../../../../../../../srcCommon/push/pushHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../../shared/helpers/src/utilities/asyncCancelHelpers';
import {makeGetDynamicColumns} from '../common/tableSelectors';
import {ContactsTable} from './contactsTable';

/*
 * Props.
 */

interface ContactsTableContainerPublicProps {
  panelId: ContactsManagerPanelId;
  multiSelectHeaderActions: ReactNode;
  renderEmpty: Renderer;
  queryOptions: FetchCardsByNamespaceOptions;
  shouldHideAccountsColumn?: boolean;
}
type ContactsTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ContactsTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type ContactsTableContainerComponentProps = ContactsTableContainerPublicProps &
  ContactsTableContainerStateProps &
  ContactsTableContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetContactTableStatus() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsContactsTableState(tableState)) {
      return undefined;
    }
    return tableState.status;
  });
}

/*
 * Component.
 */

export const ContactsTableContainerComponent: FC<ContactsTableContainerComponentProps> = (props) => {
  const {
    cardIds,
    multiSelectHeaderActions,
    shouldHideAccountsColumn,
    isInMultiSelectMode,
    selectedCardIds,
    tableStatus,
    dynamicColumns,
    paginationData,
    onFetchTableDataAsync,
    renderEmpty,
    setUpContactsTable,
    cleanup,
    addSelectedCardIds,
    removeSelectedCardIds,
  } = props;

  useInitialEffect(() => {
    setUpContactsTable(undefined, true);
    // Listen for the component to be unmounted and clean it up.
    return () => {
      cleanup();
    };
  });

  const onReset = () => {
    cleanup();
    setUpContactsTable(undefined, true);
  };

  // Listen for job_updated events for csv import jobs. If one completes, we should refresh the table.
  usePushEvent(PushEventTypesEnum.JOB_UPDATED, ApiJob, (job) => {
    // The job must be a csv import job, completed and in a done state.
    if (!isContactsCsvImportJob(job) || computeJobProgress(job) < 1 || job.status !== JobStatusesEnum.DONE) {
      return;
    }
    onReset();
  });

  // The table can't fetch until it's set up in Redux.
  if (tableStatus === undefined) {
    return null;
  }

  return (
    <ContactsTable
      cardIds={cardIds}
      shouldHideAccountsColumn={shouldHideAccountsColumn}
      multiSelectHeaderActions={multiSelectHeaderActions}
      dynamicColumns={dynamicColumns}
      isInMultiSelectMode={isInMultiSelectMode}
      selectedCardIds={selectedCardIds}
      tableStatus={tableStatus}
      paginationData={paginationData}
      renderEmpty={renderEmpty}
      onFetchTableDataAsync={onFetchTableDataAsync}
      onReset={onReset}
      addSelectedCardIds={addSelectedCardIds}
      removeSelectedCardIds={removeSelectedCardIds}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactsTableContainerPublicProps>()(() => ({
  cardIds: makeGetContactsTableCardIds(),
  tableStatus: makeGetContactTableStatus(),
  isInMultiSelectMode: makeGetIsInMultiSelectMode(),
  selectedCardIds: makeGetSelectedCardIds(),
  dynamicColumns: makeGetDynamicColumns(),
  paginationData: makeGetContactsManagerTableOffsetPaginationData(makeGetContactsManagerTableState()),
}));

const mapDispatchToProps = makeMapDispatchToProps<ContactsTableContainerPublicProps>()(
  ({panelId, queryOptions}) => ({
    setUpContactsTable: contactsManagerSetUpContactsTableAction,
    cleanup: contactsManagerTableCleanupAction,
    addSelectedCardIds: contactsManagerTableAddSelectedCardIdsAction,
    removeSelectedCardIds: contactsManagerTableRemoveSelectedCardIdsAction,
    onFetchTableDataAsync: (cancelToken: CancelToken) =>
      contactsManagerFetchContactsTableDataAsyncAction(panelId, queryOptions, cancelToken),
  }),
);

export const ContactsTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactsTableContainerComponent);
